import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import * as StringFormatter from '@/Utility/StringFormatter';

export default class Label extends Control {

    protected jQueryControl: JQuery;
    protected textElement: JQuery;
    protected prefixElement: JQuery;
    protected postfixElement: JQuery;

    public constructor(protected viewModel: LabelViewModelType, private element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.createWrapperElement();

        this.textElement = this.createTextElement();
        this.jQueryControl.append(this.textElement);

        if (this.viewModel.prefix) {
            this.prefixElement = this.createAffixElement(true);
            this.jQueryControl.prepend(this.prefixElement);
        }

        if (this.viewModel.postfix) {
            this.postfixElement = this.createAffixElement(false);
            this.jQueryControl.append(this.postfixElement);
        }

        this.widget(null);

        return this.jQueryControl;
    }

    private createWrapperElement(): JQuery {
        const elem = this.element || $('<div>');
        elem.addClass('label-wrapper');
        return elem;
    }

    private createTextElement(): JQuery {
        return $('<span>').addClass('label-text');
    }

    private createAffixElement(prefix: boolean): JQuery {
        return $('<span>').addClass(prefix ? 'label-prefix' : 'label-postfix');
    }

    protected onWidgetCreatedCallback(widget: never): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'label',
            locator: this.viewModel.locator,
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            visible: this.viewModel.isVisible,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            styles: this.definePureComputed({
                read: () => {
                    let styles = [];

                    if (this.viewModel.isErrorLabel() === true) {
                        styles.push('red');
                    } else if (this.viewModel.isInfoLabel() === true) {
                        styles.push('info');
                    } else {
                        styles.push('default');
                    }

                    return styles;
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            }),
        });

        this.applyBindings(this.textElement, {
            html: this.definePureComputed({
                read: () => {
                    let caption = this.viewModel.caption() || this.viewModel.placeholder();
                    let parsed = this.parseCaption(caption);

                    return parsed;
                }
            }),
        });

        if (this.prefixElement) {
            this.applyAffixBindings(this.prefixElement, this.viewModel.prefix);
        }

        if (this.postfixElement) {
            this.applyAffixBindings(this.postfixElement, this.viewModel.postfix);
        }
    }

    private applyAffixBindings(element: JQuery, viewModel: LabelViewModelType): void {
        this.applyBindings(element, {
            control: {
                uiGenerator: 'Label',
                viewModel: viewModel
            }
        });
    }

    private parseCaption(caption) {
        if (!_.isString(caption)) {
            return caption;
        }
        caption = StringFormatter.escapeHtml(caption, true).replace(/  /g, ' \u00a0').replace(/(?:\r\n|\r|\n)/g, '<br>');
        return caption;
    }
}

ComponentManager.setControl('Label', Label, 'RADPresentation.HTMLClient.DefaultViewExtensions');